import { useState } from 'react';
import { failureOf, said } from '@/api/errors';
import { useCompanies, useFlexWrite } from '@/api/hooks';
import type { WireFlexEntity, WireFlexRow } from '@/api/wire';
import { useMeOrThrow } from '@/app/MeContext';
import { Dialog } from '@/ui/Dialog';
import { Field, TextInput } from '@/ui/primitives';
import { Refusal } from '@/ui/Refusal';
import { useToast } from '@/ui/Toast';
import { Select } from '@/ui/fields';
import { useTechnical } from '@/app/technical';
import { humanise } from '@/ui/words';

/**
 * Writing one of the department's own records (ADR-0110).
 *
 * The form is the live table's fields and nothing else: the kind decides
 * what may be written, and the route checks it again. A company is picked,
 * never typed; a reference to another record is what a feature writes, so
 * it is shown and not offered. Whatever the write sets off — an order
 * captured when a deal is won, Customer.io told a sample went out — acts as
 * the department's agent, on this person's behalf.
 */

const NOT_TYPED = new Set(['id', 'created_at', 'meta']);

function editable(kind: WireFlexEntity) {
  return kind.fields.filter((f) => !NOT_TYPED.has(f.name) && (f.name === 'company_id' || !f.references));
}

function label(field: string): string {
  return field.replace(/_id$/, '').replace(/_at$/, '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

function asInput(type: string, value: unknown): string {
  if (value === null || value === undefined) return '';
  if (type.startsWith('timestamp') || type === 'date') return String(value).slice(0, 10);
  return String(value);
}

type Value = string | number | boolean | null;

function asValue(type: string, raw: string): Value {
  const text = raw.trim();
  if (text === '') return null;
  if (type === 'boolean') return text === 'true';
  if (type.startsWith('timestamp')) return new Date(`${text}T12:00:00Z`).toISOString();
  return text;
}

export function EditFlexRecord({ kind, row, onClose }: { kind: WireFlexEntity; row?: WireFlexRow | undefined; onClose: () => void }) {
  const tech = useTechnical();
  const me = useMeOrThrow();
  const write = useFlexWrite(me.department);
  const companies = useCompanies('');
  const toast = useToast();
  const fields = editable(kind);
  const [draft, setDraft] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((f) => [f.name, asInput(f.type, row?.[f.name])])),
  );
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker={tech ? `${kind.entity} · ${kind.specId} v${kind.specVersion}` : humanise(kind.entity)}
      title={row ? `Edit this ${kind.entity.toLowerCase()}` : `A new ${kind.entity.toLowerCase()}`}
      secondary={{ label: 'Cancel', onClick: onClose }}
      primary={{
        label: 'Save',
        disabled: write.isPending,
        onClick: () => {
          const values: Record<string, Value> = {};
          for (const f of fields) {
            const before = asInput(f.type, row?.[f.name]);
            // Only what changed is sent, so a save does not set off a
            // feature over a field nobody touched.
            if (row && draft[f.name] === before) continue;
            if (!row && (draft[f.name] ?? '').trim() === '') continue;
            values[f.name] = asValue(f.type, draft[f.name] ?? '');
          }
          if (Object.keys(values).length === 0) return row ? onClose() : setError('Fill in at least one field.');
          write.mutate(
            { entity: kind.entity, ...(row ? { id: row.id } : {}), values },
            { onSuccess: () => { toast({ text: 'Saved.' }); onClose(); }, onError: (e) => setError(said(failureOf(e))) },
          );
        },
      }}
    >
      <div className="flex flex-col gap-4" data-testid="flex-edit">
        {fields.map((f) =>
          f.name === 'company_id' ? (
            <Field key={f.name} label="Company">
              <Select
                aria-label="Company"
                
                value={draft[f.name] ?? ''}
                onChange={(e) => setDraft({ ...draft, [f.name]: e.target.value })}
              >
                <option value="">Pick a company</option>
                {(companies.data?.companies ?? []).map((c) => (
                  <option key={c.id} value={c.id}>{c.displayName ?? c.legalName}</option>
                ))}
              </Select>
            </Field>
          ) : (
            <Field key={f.name} label={label(f.name)}>
              <TextInput
                aria-label={label(f.name)}
                type={f.type.startsWith('timestamp') || f.type === 'date' ? 'date' : 'text'}
                inputMode={f.type.startsWith('numeric') || f.type === 'integer' ? 'decimal' : undefined}
                value={draft[f.name] ?? ''}
                onChange={(e) => setDraft({ ...draft, [f.name]: e.target.value })}
              />
            </Field>
          ),
        )}
        {error && <p className="m-0 text-dense text-danger">{error}</p>}
        {write.isError && <Refusal failure={failureOf(write.error)} compact />}
      </div>
    </Dialog>
  );
}

/**
 * A deal decided: won or lost, and when (proposal 0008). Where the live
 * Deal carries a loss reason (deal_pipeline v2), losing asks for it, so
 * Insight can say why deals are lost and not only where.
 */
export function DecideDeal({ kind, row, outcome, onClose }: { kind: WireFlexEntity; row: WireFlexRow; outcome: 'won' | 'lost'; onClose: () => void }) {
  const me = useMeOrThrow();
  const write = useFlexWrite(me.department);
  const toast = useToast();
  const hasReason = outcome === 'lost' && kind.fields.some((f) => f.name === 'loss_reason');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const has = (name: string) => kind.fields.some((f) => f.name === name);
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="Deal"
      title={outcome === 'won' ? 'Mark this deal won' : 'Mark this deal lost'}
      secondary={{ label: 'Cancel', onClick: onClose }}
      primary={{
        label: outcome === 'won' ? 'Won' : 'Lost',
        disabled: write.isPending,
        onClick: () => {
          if (hasReason && reason.trim() === '') return setError('Say why, in a few words.');
          write.mutate(
            {
              entity: kind.entity,
              id: row.id,
              values: {
                status: outcome,
                ...(has('decided_at') ? { decided_at: new Date().toISOString() } : {}),
                ...(hasReason ? { loss_reason: reason.trim() } : {}),
              },
            },
            { onSuccess: () => { toast({ text: outcome === 'won' ? 'Marked won.' : 'Marked lost.' }); onClose(); }, onError: (e) => setError(said(failureOf(e))) },
          );
        },
      }}
    >
      <p className="m-0 text-body">
        {outcome === 'won'
          ? 'Whatever the department runs on a won deal — an order captured, a message to its owner — runs now, as its agent, on your behalf.'
          : 'The deal stays on record, lost. Nothing is deleted.'}
      </p>
      {hasReason && (
        <Field label="Why it was lost" error={error ?? undefined}>
          <TextInput aria-label="Why it was lost" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Price, timing, went with a competitor…" />
        </Field>
      )}
      {!hasReason && error && <p className="m-0 text-dense text-danger">{error}</p>}
      {write.isError && <Refusal failure={failureOf(write.error)} compact />}
    </Dialog>
  );
}
